<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onDestroy } from 'svelte';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { AlertV2, ButtonV2, Container, InputV2, Modal, Text } from '@Shelly/shelly-design-system';
	import { IR_CODE_NAME_EXISTS_ERROR, learnIRCode } from 'src/helpers/ir_controller_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';

	export let device: Device;
	export let irDeviceId: number;
	export let initialName = '';
	export let irCodesCount = 0;
	export let showModal = false;

	const LEARN_TIMEOUT_SECONDS = 15;

	let codeName = '';
	let phase: 'idle' | 'learning' | 'timeout' | 'error' = 'idle';
	let remainingSeconds = 0;
	let errorMessage = '';
	let codesCountBeforeLearning = 0;
	let timer: ReturnType<typeof setInterval> | null = null;

	const stopTimer = () => {
		if (timer) {
			clearInterval(timer);
			timer = null;
		}
	};

	const resetForm = () => {
		codeName = initialName || '';
		phase = 'idle';
		remainingSeconds = 0;
		errorMessage = '';
		codesCountBeforeLearning = 0;
		stopTimer();
	};

	$: if (showModal) resetForm();

	const handleClose = () => {
		showModal = false;
		phase = 'idle';
		stopTimer();
	};

	$: if (phase === 'learning' && irCodesCount > codesCountBeforeLearning) handleClose();

	const getFormAlert = () => {
		switch (phase) {
			case 'timeout':
				return {
					icon: 'fas fa-exclamation-triangle text-warning',
					heading: $format('ir_controller_learn_timeout', {
						default: 'Learning timed out. Try again',
					}),
				};
			case 'error':
				return {
					icon: 'fas fa-exclamation-circle text-danger',
					heading:
						errorMessage ||
						$format('ir_controller_learn_failed', { default: 'Learning failed' }),
				};
			default:
				return null;
		}
	};

	const startCountdown = () => {
		remainingSeconds = LEARN_TIMEOUT_SECONDS;
		timer = setInterval(() => {
			remainingSeconds -= 1;
			if (remainingSeconds > 0) return;
			stopTimer();
			if (phase === 'learning') phase = 'timeout';
		}, 1000);
	};

	const handleStartLearning = async () => {
		const name = codeName.trim();
		if (!name || phase === 'learning') return;

		codesCountBeforeLearning = irCodesCount;
		phase = 'learning';
		errorMessage = '';
		startCountdown();

		const response: SettingRequest = await learnIRCode(
			device,
			irDeviceId,
			name,
			LEARN_TIMEOUT_SECONDS,
		);
		if (phase !== 'learning') return;
		if (response?.isok) return;
		stopTimer();

		const error = response?.error;
		const deviceError = error?.device_error;
		const msg = deviceError?.message || (typeof error === 'string' ? error : error?.message) || '';
		if (deviceError?.code === IR_CODE_NAME_EXISTS_ERROR && /already exists/i.test(msg)) {
			phase = 'error';
			errorMessage = $format('ir_controller_code_name_exists', {
				default: 'A command with this name already exists.',
			});
			return;
		}
		if (/timeout/i.test(msg)) {
			phase = 'timeout';
			return;
		}
		phase = 'error';
		errorMessage = msg;
		onSettingsResponse(response, device);
	};

	onDestroy(stopTimer);
</script>

<Modal
	{showModal}
	title={$format('ir_controller_learn_ir_code', { default: 'Learn IR Code' })}
	on:close={handleClose}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		{#if phase === 'learning'}
			<AlertV2
				isHeadingBold
				customIcon="fas fa-info-circle"
				heading={$format('ir_controller_learn_point_remote', {
					default: 'Point the remote at the device and press the button...',
				})}
			/>
			<Text class="mt-s text-center" styleType="body">
				{$format('ir_controller_learn_waiting', {
					default: 'Time left to press a button on the remote',
				})}
			</Text>
			<Text class="mt-xs text-center" styleType="h2">{remainingSeconds}s</Text>
		{:else}
			{@const formAlert = getFormAlert()}
			{#if formAlert}
				<AlertV2
					class="mb-s"
					isHeadingBold
					customIcon={formAlert.icon}
					heading={formAlert.heading}
				/>
			{/if}
			<AlertV2
				class="mb-s"
				customIcon="fas fa-info-circle"
				heading={$format('ir_controller_learn_how_message', {
					default:
						'Name the command, then tap Start learning. Point the remote at the IR Controller and press the button.',
				})}
			/>
			<InputV2
				id="ir-controller-learn-code-name"
				size="m"
				label={$format('ir_controller_code_name', { default: 'Command name' })}
				aria-label={$format('ir_controller_code_name', { default: 'Command name' })}
				info={$format('ir_controller_code_name_placeholder', {
					default: 'e.g. power, vol_up, ch_down',
				})}
				icon={null}
				maxlength="255"
				bind:value={codeName}
			/>
		{/if}
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			size="m"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			on:click={handleClose}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		{#if phase !== 'learning'}
			<ButtonV2
				size="m"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				disabled={!codeName.trim()}
				aria-label={$format('ir_controller_start_learning', { default: 'Start learning' })}
				on:click={handleStartLearning}
			>
				{$format('ir_controller_start_learning', { default: 'Start learning' })}
			</ButtonV2>
		{/if}
	</div>
</Modal>
